import { useEffect, useRef, useState } from 'react';
import { Input, Radio, Checkbox, Button, Space, Typography, Alert, Divider } from 'antd';
import {
  CloseOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { space } from '@viq-admin/ui';
import type { DiscoveryQuestion, DiscoveryQuestionType, QuestionPayload } from './types';

interface DraftOption {
  key: string;
  text: string;
}

export interface QuestionFormProps {
  initial?: DiscoveryQuestion | null;
  /** Inventory editor shows the "default" checkbox; the conference editor hides it. */
  showDefault?: boolean;
  submitting?: boolean;
  onSubmit: (payload: QuestionPayload) => void | Promise<void>;
  onCancel: () => void;
  /**
   * Hides this form's own Cancel button. Set by the console `QuestionDetail`
   * bare-form embedding (`QuestionEdit`) — the shared `DetailPage` already
   * renders a single Cancel in its header (routed through its discard
   * guard), so a second Cancel button here would both be redundant and, more
   * importantly, would bypass that guard entirely if clicked. The standalone
   * `QuestionCreate`/`QuestionEdit`-as-a-page callers leave this unset.
   */
  hideCancel?: boolean;
  /**
   * Fires `true` the first time ANY field changes after mount (never back to
   * `false` — mirrors the antd `Form`'s `onValuesChange` "touched" semantics
   * the other bare edit forms use). `QuestionForm` has no antd `<Form>` under
   * it (it's a hand-rolled stateful component), so this is the only signal
   * the embedding `DetailPage`'s discard guard has for "has this session been
   * edited". Not called for the standalone Create/Edit-as-page callers (they
   * don't pass it).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const MAX_OPTIONS = 12;

/**
 * One editable answer option with move-up / move-down / remove controls.
 *
 * NOTE (portal delta): the customer portal reorders options via `@dnd-kit`
 * drag-and-drop. That package is not a declared dependency of
 * `apps/console`, so the console uses explicit up/down buttons instead —
 * same result (reordered `options` array), no phantom dependency.
 */
function OptionRow({
  option,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}: {
  option: DraftOption;
  index: number;
  count: number;
  onChange: (text: string) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, marginBottom: space.sm }}>
      <Button
        type="text"
        size="small"
        aria-label="Move option up"
        icon={<ArrowUpOutlined />}
        disabled={index === 0}
        onClick={() => onMove(-1)}
      />
      <Button
        type="text"
        size="small"
        aria-label="Move option down"
        icon={<ArrowDownOutlined />}
        disabled={index === count - 1}
        onClick={() => onMove(1)}
      />
      <Input
        placeholder="Option text"
        value={option.text}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button type="text" aria-label="Remove option" icon={<CloseOutlined />} onClick={onRemove} />
    </div>
  );
}

export function QuestionForm({
  initial,
  showDefault = false,
  submitting,
  onSubmit,
  onCancel,
  hideCancel = false,
  onDirtyChange,
}: QuestionFormProps) {
  const seq = useRef(0);
  const mkKey = () => `opt-${seq.current++}`;

  const [text, setText] = useState(initial?.text ?? '');
  const [type, setType] = useState<DiscoveryQuestionType>(initial?.type ?? 'choice');
  const [allowMultiple, setAllowMultiple] = useState(initial?.allowMultiple ?? false);
  const [hasOtherOption, setHasOtherOption] = useState(initial?.hasOtherOption ?? false);
  const [required, setRequired] = useState(initial?.required ?? false);
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [options, setOptions] = useState<DraftOption[]>(
    initial?.options?.length
      ? initial.options.map((o) => ({ key: mkKey(), text: o.text }))
      : [{ key: mkKey(), text: '' }, { key: mkKey(), text: '' }],
  );
  const [error, setError] = useState<string | null>(null);

  // Skip the mount-time run (initial state seeded from `initial`, not a user
  // edit) — every render after that reports dirty. Never resets to `false`
  // here; a fresh session gets a fresh mount (and thus a fresh ref) because
  // the embedding DetailPage unmounts editView entirely on exit from edit.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onDirtyChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, type, allowMultiple, hasOtherOption, required, isDefault, options]);

  function moveOption(index: number, dir: -1 | 1) {
    setOptions((prev) => {
      const to = index + dir;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  function addOption() {
    setOptions((prev) => (prev.length >= MAX_OPTIONS ? prev : [...prev, { key: mkKey(), text: '' }]));
  }

  function handleSave() {
    setError(null);
    if (!text.trim()) {
      setError('Question text is required');
      return;
    }
    if (type === 'choice') {
      const clean = options.map((o) => o.text.trim()).filter(Boolean);
      if (clean.length < 2) {
        setError('Choice questions need at least 2 options');
        return;
      }
      if (clean.length > MAX_OPTIONS) {
        setError(`Choice questions allow at most ${MAX_OPTIONS} options`);
        return;
      }
      void onSubmit({
        text: text.trim(),
        type,
        allowMultiple,
        hasOtherOption,
        required,
        ...(showDefault ? { isDefault } : {}),
        options: clean.map((t) => ({ text: t })),
      });
      return;
    }
    void onSubmit({
      text: text.trim(),
      type,
      required,
      ...(showDefault ? { isDefault } : {}),
    });
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Text strong>Type</Typography.Text>
        <div>
          <Radio.Group value={type} onChange={(e) => setType(e.target.value)} style={{ marginTop: space.xs }}>
            <Radio value="choice">Choice</Radio>
            <Radio value="free_text">Free-text</Radio>
          </Radio.Group>
        </div>
      </div>

      <label style={{ display: 'block' }}>
        <Typography.Text strong>Question</Typography.Text>
        <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} style={{ marginTop: space.xs }} />
      </label>

      {type === 'choice' && (
        <Checkbox checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)}>
          Allow multiple answers
        </Checkbox>
      )}
      <Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)}>Required</Checkbox>
      {showDefault && (
        <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}>
          Add to new conferences by default
        </Checkbox>
      )}

      {type === 'choice' ? (
        <div>
          <Divider style={{ margin: '8px 0' }} />
          <Typography.Text strong>Answer options </Typography.Text>
          <Typography.Text type="secondary">(2–12 · use ↑ ↓ to reorder)</Typography.Text>
          <div style={{ marginTop: space.sm }}>
            {options.map((o, i) => (
              <OptionRow
                key={o.key}
                option={o}
                index={i}
                count={options.length}
                onChange={(t) => setOptions((prev) => prev.map((p) => (p.key === o.key ? { ...p, text: t } : p)))}
                onMove={(dir) => moveOption(i, dir)}
                onRemove={() => setOptions((prev) => prev.filter((p) => p.key !== o.key))}
              />
            ))}
            <Button size="small" icon={<PlusOutlined />} onClick={addOption} disabled={options.length >= MAX_OPTIONS}>
              Add option
            </Button>
          </div>
          <Checkbox checked={hasOtherOption} onChange={(e) => setHasOtherOption(e.target.checked)} style={{ marginTop: space.md }}>
            Include an "Other: ___" free-text option
          </Checkbox>
        </div>
      ) : (
        <Alert
          type="info"
          message="No options. The rep types a free-form answer on the tear sheet — ideal for grabbing a cell phone, a referral name, or a one-off detail."
        />
      )}

      {error && <Alert type="error" message={error} />}

      <Space>
        <Button type="primary" loading={submitting} onClick={handleSave}>Save</Button>
        {!hideCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Space>
    </Space>
  );
}
